// QUOTE LINES FILTER (quote-lines-filter, 2026-10-06) -- the PURE logic.
//
// The filter chooses which lines the table DRAWS (and pages through). It never
// chooses which lines COUNT: the totals row, the header total, GP, GST, the
// summary strip, save and the PDF all read the whole quote (owner decision).
// The one figure that follows the filter is the clearly labelled "Subtotal of
// shown lines", a plain sum of the engine's stored sell values.

export interface FilterableLine {
  partNo?: string;
  description?: string;
  oem?: string;
  manufacturer?: string;
}

/** The words typed, lower-cased. Empty / whitespace = no filter. */
export function filterTerms(text: string): string[] {
  return (text ?? "").toLowerCase().split(/\s+/).filter(Boolean);
}

export function isFiltering(text: string): boolean {
  return filterTerms(text).length > 0;
}

/** Part number, description and brand (oem / manufacturer), case-insensitive,
 *  "contains". Several words must ALL appear (in any of those fields), so
 *  "netrack pdu" finds a Netrack PDU. */
export function lineMatchesFilter(line: FilterableLine, text: string): boolean {
  const terms = filterTerms(text);
  if (terms.length === 0) return true;
  const hay = [line.partNo, line.description, line.oem, line.manufacturer]
    .map((v) => (v ?? "").toLowerCase())
    .join("\n");
  return terms.every((t) => hay.includes(t));
}

export function filterLines<T extends FilterableLine>(lines: readonly T[], text: string): T[] {
  return isFiltering(text) ? lines.filter((l) => lineMatchesFilter(l, text)) : [...lines];
}

/** "Showing 12 of 182 lines" (singular-safe). */
export function showingLabel(shown: number, total: number): string {
  return `Showing ${shown} of ${total} ${total === 1 ? "line" : "lines"}`;
}

/** The sum of the engine's stored sell value (`extended_sp`) over `lineNos`.
 *  null when any of them has no engine number yet: a partial sum would read as
 *  a subtotal of the shown lines and is not. Aggregation of stored values only. */
export function shownSubtotal(
  lineNos: readonly number[],
  extendedSp: (lineNo: number) => number | undefined,
): number | null {
  if (lineNos.length === 0) return 0;
  let sum = 0;
  for (const n of lineNos) {
    const v = extendedSp(n);
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
    sum += v;
  }
  return sum;
}
